import React, { useState, MouseEvent } from 'react';
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';

export type ExportDataMap = {
  [label: string]: any[] | (() => Promise<any[]>) | (() => void);
};

interface ExportMenuProps {
  dataMap: ExportDataMap;
  filenamePrefixMap?: { [label: string]: string };
  disabled?: boolean;
}

const ExportMenu: React.FC<ExportMenuProps> = ({
  dataMap,
  filenamePrefixMap = {},
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const t = useCommonTranslation();

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const exportToCsv = async (
    dataSource: any[] | (() => Promise<any[]>) | (() => void),
    label: string,
  ) => {
    const data =
      typeof dataSource === 'function' ? await dataSource() : dataSource;
    if (!data || !data.length) return;

    const headers = Object.keys(data[0]);
    const csvRows = [
      headers.join(','),
      ...data.map((row) =>
        headers.map((field) => JSON.stringify(row[field] ?? '')).join(','),
      ),
    ];

    const blob = new Blob([csvRows.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeLabel = label.toLowerCase().replace(/\s+/g, '-');
    const prefix = filenamePrefixMap[label] ?? 'export';

    link.href = url;
    link.setAttribute('download', `${prefix}-${safeLabel}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Tooltip title={t('settings.exportMenuTooltipTitle')}>
        <span>
          <IconButton
            aria-label="export options"
            aria-controls="export-menu"
            aria-haspopup="true"
            onClick={handleClick}
            disabled={disabled}>
            <MoreVertIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        id="export-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        {Object.entries(dataMap).map(([label, data]) => (
          <MenuItem
            key={label}
            onClick={() => {
              handleClose();
              exportToCsv(data, label);
            }}
            disabled={disabled}>
            {t('settings.exportWithLabel', { label })}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ExportMenu;
